import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { BoardTemplate, Workspace } from '../../mainApi';
import {
  copyMarketplaceTemplate,
  createTemplateInstance,
  deleteTemplate,
  getMarketplaceTemplates,
  getTemplates,
  shareTemplate,
  TemplateInstanceInput,
  TemplateShareInput,
  TemplateUpdateInput,
  updateTemplate,
} from '../../services/templates.service';
import { isActiveSpaceLimitError } from '../../services/activeSpaceLimit.service';
import { useSpacesStore } from '../../stores/spacesStore';
import { useWorkspacesStore } from '../../stores/workspacesStore';
import { useAuthStore } from '../../stores/authStore';
import { hasReachedActiveOwnedSpaceLimit } from '../../utils/activeSpaceLimit.utils';
import { ActiveSpaceLimitDialog } from '../notifications/ActiveSpaceLimitDialog';
import { CreateTemplateInstanceDialog } from './CreateTemplateInstanceDialog';
import { DeleteTemplateDialog } from './DeleteTemplateDialog';
import { EditTemplateDialog } from './EditTemplateDialog';
import { ShareTemplateDialog } from './ShareTemplateDialog';
import { TemplateCard } from './TemplateCard';
import { trackAnalytics, trackAnalyticsOnce } from '@dotstorming/analytics';

export type TemplatesTab = 'my' | 'marketplace';

const getSortedTemplates = (templates: BoardTemplate[]) =>
  [...templates].sort((left, right) => (left.name ?? '').localeCompare(right.name ?? ''));

type TemplatesListProps = {
  activeTab: TemplatesTab;
};

export const TemplatesList = ({ activeTab }: TemplatesListProps) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<BoardTemplate[]>([]);
  const [marketplaceTemplates, setMarketplaceTemplates] = useState<BoardTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMarketplace, setIsLoadingMarketplace] = useState(true);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(null);
  const [copyingTemplateId, setCopyingTemplateId] = useState<string | null>(null);
  const [updatingTemplateId, setUpdatingTemplateId] = useState<string | null>(null);
  const [sharingTemplateId, setSharingTemplateId] = useState<string | null>(null);
  const [templatePendingDelete, setTemplatePendingDelete] = useState<BoardTemplate | null>(null);
  const [templatePendingEdit, setTemplatePendingEdit] = useState<BoardTemplate | null>(null);
  const [templatePendingShare, setTemplatePendingShare] = useState<BoardTemplate | null>(null);
  const [templatePendingCreate, setTemplatePendingCreate] = useState<BoardTemplate | null>(null);
  const [templatePendingCreateSource, setTemplatePendingCreateSource] = useState<
    'user' | 'marketplace'
  >('user');
  const [isActiveSpaceLimitDialogOpen, setIsActiveSpaceLimitDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketplaceError, setMarketplaceError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { user } = useAuthStore();
  const { workspaces } = useWorkspacesStore();
  const { loadSpaces, spaces } = useSpacesStore();
  const sortedTemplates = useMemo(() => getSortedTemplates(templates), [templates]);
  const sortedMarketplaceTemplates = useMemo(
    () => getSortedTemplates(marketplaceTemplates),
    [marketplaceTemplates]
  );
  const hasReachedActiveSpaceLimit = useMemo(
    () => hasReachedActiveOwnedSpaceLimit(user, spaces),
    [spaces, user]
  );

  useEffect(() => {
    let isActive = true;

    const loadTemplates = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const nextTemplates = await getTemplates();

        if (isActive) {
          setTemplates(nextTemplates);
        }
      } catch (loadError) {
        if (isActive) {
          setError(loadError instanceof Error ? loadError.message : 'Unable to load templates.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadTemplates();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadMarketplaceTemplates = async () => {
      setIsLoadingMarketplace(true);
      setMarketplaceError(null);

      try {
        const nextTemplates = await getMarketplaceTemplates();

        if (isActive) {
          setMarketplaceTemplates(nextTemplates);
        }
      } catch (loadError) {
        if (isActive) {
          setMarketplaceError(
            loadError instanceof Error ? loadError.message : 'Unable to load marketplace templates.'
          );
        }
      } finally {
        if (isActive) {
          setIsLoadingMarketplace(false);
        }
      }
    };

    void loadMarketplaceTemplates();

    return () => {
      isActive = false;
    };
  }, []);

  const handleConfirmDeleteTemplate = async () => {
    const template = templatePendingDelete;

    if (!template) {
      return;
    }

    setDeletingTemplateId(template.id);
    setError(null);

    try {
      await deleteTemplate(template.id);
      setTemplates((currentTemplates) =>
        currentTemplates.filter((currentTemplate) => currentTemplate.id !== template.id)
      );
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete template.');
    } finally {
      setDeletingTemplateId(null);
      setTemplatePendingDelete(null);
    }
  };

  const handleCreateTemplateInstance = async (
    payload: TemplateInstanceInput,
    workspace: Workspace | null
  ) => {
    const template = templatePendingCreate;

    if (!template) {
      return;
    }

    setCreatingTemplateId(template.id);
    setError(null);
    setMarketplaceError(null);

    try {
      const path = await createTemplateInstance(template.id, payload, templatePendingCreateSource);

      if (path) {
        const spaceType = template.spaceType ?? 'board';
        trackAnalytics('template_selected', {
          template_source: templatePendingCreateSource,
          space_type: spaceType,
          template_category: template.categories?.[0],
        });
        if (spaceType === 'board') {
          trackAnalytics('board_created', {
            creation_source: 'template',
            from_template: true,
            workspace_context: workspace ? 'workspace' : 'personal',
          });
          const ownedBoardCount = spaces.filter(
            (space) => space.type === 'board' && space.isOwner === true
          ).length;
          if (ownedBoardCount === 1) {
            trackAnalyticsOnce('second_board_created', 'second_board_created', {
              creation_source: 'template',
              from_template: true,
            });
          }
        }
        void loadSpaces();
        const workspaceId = workspace?.id;
        window.location.href = workspaceId
          ? `${path}?workspaceId=${encodeURIComponent(workspaceId)}`
          : path;
        return;
      }

      throw new Error('Template instance was created without a space URL.');
    } catch (createError) {
      if (isActiveSpaceLimitError(createError)) {
        setCreatingTemplateId(null);
        setTemplatePendingCreate(null);
        setIsActiveSpaceLimitDialogOpen(true);
        return;
      }

      const message =
        createError instanceof Error
          ? createError.message
          : 'Unable to create space from template.';

      if (templatePendingCreateSource === 'marketplace') {
        setMarketplaceError(message);
      } else {
        setError(message);
      }

      setCreatingTemplateId(null);
    }
  };

  const handleCopyMarketplaceTemplate = async (template: BoardTemplate) => {
    setCopyingTemplateId(template.id);
    setMarketplaceError(null);
    setSuccessMessage(null);

    try {
      const copiedTemplate = await copyMarketplaceTemplate(template.id);
      setTemplates((currentTemplates) => [copiedTemplate, ...currentTemplates]);
      setSuccessMessage(`${copiedTemplate.name || 'Template'} copied to My templates.`);
    } catch (copyError) {
      setMarketplaceError(
        copyError instanceof Error ? copyError.message : 'Unable to copy marketplace template.'
      );
    } finally {
      setCopyingTemplateId(null);
    }
  };

  const handleUpdateTemplate = async (input: TemplateUpdateInput) => {
    const template = templatePendingEdit;

    if (!template) {
      return;
    }

    setUpdatingTemplateId(template.id);
    setEditError(null);
    setSuccessMessage(null);

    try {
      const updatedTemplate = await updateTemplate(template.id, input);
      setTemplates((currentTemplates) =>
        currentTemplates.map((currentTemplate) =>
          currentTemplate.id === updatedTemplate.id
            ? { ...currentTemplate, ...updatedTemplate }
            : currentTemplate
        )
      );
      setTemplatePendingEdit(null);
      setSuccessMessage('Template updated.');
    } catch (updateError) {
      setEditError(
        updateError instanceof Error ? updateError.message : 'Unable to update template.'
      );
    } finally {
      setUpdatingTemplateId(null);
    }
  };

  const handleShareTemplate = async (input: TemplateShareInput) => {
    const template = templatePendingShare;

    if (!template) {
      return;
    }

    setSharingTemplateId(template.id);
    setShareError(null);
    setSuccessMessage(null);

    try {
      const result = await shareTemplate(template.id, input);
      const sentCount = result.invited.length;

      setTemplatePendingShare(null);
      setSuccessMessage(
        sentCount > 0 ? `Template invitation sent.` : 'No new invitations were sent.'
      );
    } catch (shareTemplateError) {
      setShareError(
        shareTemplateError instanceof Error
          ? shareTemplateError.message
          : 'Unable to share template.'
      );
    } finally {
      setSharingTemplateId(null);
    }
  };

  const isDeletingTemplate = Boolean(deletingTemplateId);
  const isCreatingTemplate = Boolean(creatingTemplateId);
  const isUpdatingTemplate = Boolean(updatingTemplateId);
  const visibleTemplates =
    activeTab === 'marketplace' ? sortedMarketplaceTemplates : sortedTemplates;
  const activeError = activeTab === 'marketplace' ? marketplaceError : error;
  const activeIsLoading = activeTab === 'marketplace' ? isLoadingMarketplace : isLoading;
  const emptyTitle =
    activeTab === 'marketplace' ? 'No marketplace templates' : 'No templates saved';
  const emptyDescription =
    activeTab === 'marketplace'
      ? 'Marketplace templates will appear here when system templates have been added.'
      : 'Save a board as a template and it will appear here with its type, name, and description.';

  return (
    <>
      {activeIsLoading ? (
        <Stack alignItems="center" sx={{ py: 10 }}>
          <CircularProgress />
        </Stack>
      ) : null}
      {!activeIsLoading && activeError && visibleTemplates.length === 0 ? (
        <Alert severity="error">{activeError}</Alert>
      ) : null}
      {!activeIsLoading && activeError && visibleTemplates.length > 0 ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {activeError}
        </Alert>
      ) : null}
      {!activeIsLoading && !activeError && visibleTemplates.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: '#fff',
            border: '1px solid #e6e8ef',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#022b3a', mb: 0.75 }}>{emptyTitle}</Typography>
          <Typography color="text.secondary">{emptyDescription}</Typography>
        </Box>
      ) : null}
      {!activeIsLoading && visibleTemplates.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {visibleTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isDeleting={deletingTemplateId === template.id}
              isCreating={creatingTemplateId === template.id}
              isCopying={copyingTemplateId === template.id}
              isSharing={sharingTemplateId === template.id}
              isUpdating={updatingTemplateId === template.id}
              onCreate={(nextTemplate) => {
                if (hasReachedActiveSpaceLimit) {
                  setIsActiveSpaceLimitDialogOpen(true);
                  return;
                }

                setTemplatePendingCreateSource(
                  activeTab === 'marketplace' ? 'marketplace' : 'user'
                );
                setTemplatePendingCreate(nextTemplate);
              }}
              onCopy={
                activeTab === 'marketplace'
                  ? (nextTemplate) => void handleCopyMarketplaceTemplate(nextTemplate)
                  : undefined
              }
              onEdit={
                activeTab === 'my'
                  ? (nextTemplate) => {
                      setEditError(null);
                      setTemplatePendingEdit(nextTemplate);
                    }
                  : undefined
              }
              onShare={
                activeTab === 'my'
                  ? (nextTemplate) => {
                      setShareError(null);
                      setTemplatePendingShare(nextTemplate);
                    }
                  : undefined
              }
              onDelete={activeTab === 'my' ? setTemplatePendingDelete : undefined}
            />
          ))}
        </Box>
      ) : null}
      <CreateTemplateInstanceDialog
        template={templatePendingCreate}
        isCreating={isCreatingTemplate}
        workspaces={workspaces}
        onClose={() => {
          setTemplatePendingCreate(null);
          setTemplatePendingCreateSource('user');
        }}
        onSubmit={(payload, workspace) => void handleCreateTemplateInstance(payload, workspace)}
      />
      <DeleteTemplateDialog
        template={templatePendingDelete}
        isDeleting={isDeletingTemplate}
        onClose={() => setTemplatePendingDelete(null)}
        onConfirm={() => void handleConfirmDeleteTemplate()}
      />
      <EditTemplateDialog
        template={templatePendingEdit}
        isUpdating={isUpdatingTemplate}
        error={editError}
        onClose={() => {
          setTemplatePendingEdit(null);
          setEditError(null);
        }}
        onSubmit={(input) => void handleUpdateTemplate(input)}
      />
      <ShareTemplateDialog
        template={templatePendingShare}
        isSharing={Boolean(sharingTemplateId)}
        error={shareError}
        onClose={() => {
          setTemplatePendingShare(null);
          setShareError(null);
        }}
        onSubmit={(input) => void handleShareTemplate(input)}
      />
      <ActiveSpaceLimitDialog
        open={isActiveSpaceLimitDialogOpen}
        onClose={() => setIsActiveSpaceLimitDialogOpen(false)}
        onNavigateBilling={() => navigate('/billing')}
      />
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }

          setSuccessMessage(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage(null)}
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
